import type {
  CommentMention,
  IssueActivity,
  IssueComment,
  ProjectMember,
} from '@outpost/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BellOff,
  Bot,
  CircleCheck,
  MessageSquare,
  Pencil,
  RotateCcw,
  Trash2,
  UserMinus,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { useRef, useState, type KeyboardEvent } from 'react';

import { AssigneeAvatar, memberName, useProjectMembers } from '@/components/assignee';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/lib/auth';
import { activityActor, describeActivity, isAutomatic, timeline } from '@/lib/activity';
import { absoluteTime, relativeTime } from '@/lib/format';
import {
  activeMention,
  completeMention,
  spell,
  splitMentions,
  type ActiveMention,
} from '@/lib/mentions';
import { allows, useOrgRole } from '@/lib/roles';
import { cn } from '@/lib/utils';

/** How many people the `@` menu offers at once. */
const SUGGESTIONS = 6;

/**
 * The discussion on an issue, and its history.
 *
 * Comments are plain text, rendered as text: the only thing given any meaning
 * is an `@` the *server* says it delivered, which is what keeps the markup
 * honest — a name that reached nobody reads as what it is.
 *
 * Between them sit the changes to the issue — who resolved, ignored or assigned
 * it — and anything an application did on someone's behalf says which one.
 */
export function CommentThread({ projectId, issueId }: { projectId: string; issueId: string }) {
  const { client, user } = useAuth();
  const queryClient = useQueryClient();
  const members = useProjectMembers(projectId);

  // Already in the cache from the layout, so this asks nothing extra of the
  // API; it is here to learn whether the reader can moderate the thread.
  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => client.getProject(projectId),
    staleTime: 5 * 60_000,
  });
  const role = useOrgRole(project.data?.org_id);

  const comments = useQuery({
    queryKey: ['issue-comments', issueId],
    queryFn: () => client.listComments(issueId),
  });
  const activity = useQuery({
    queryKey: ['issue-activity', issueId],
    queryFn: () => client.issueActivity(issueId),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['issue-comments', issueId] });

  const post = useMutation({
    mutationFn: (body: string) => client.addComment(issueId, body),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (commentId: string) => client.deleteComment(issueId, commentId),
    onSuccess: invalidate,
  });
  const edit = useMutation({
    mutationFn: ({ commentId, body }: { commentId: string; body: string }) =>
      client.updateComment(issueId, commentId, body),
    onSuccess: invalidate,
  });

  const thread = comments.data ?? [];
  // History that fails to load leaves the discussion readable without it.
  const items = timeline(thread, activity.data ?? []);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <MessageSquare className="size-4" />
          Activity
          {thread.length > 0 && (
            <span className="tabular-nums">
              {thread.length} {thread.length === 1 ? 'comment' : 'comments'}
            </span>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {comments.isError ? (
          <p className="text-sm text-destructive">
            The discussion on this issue could not be loaded.
          </p>
        ) : items.length === 0 && !comments.isLoading ? (
          <p className="text-sm text-muted-foreground">
            No comments yet. Write what you found, and name a colleague with{' '}
            <span className="font-mono text-xs">@</span> to bring them in.
          </p>
        ) : (
          <ul className="space-y-4">
            {items.map((item) =>
              item.kind === 'activity' ? (
                <li key={`activity-${item.activity.id}`}>
                  <ActivityRow entry={item.activity} youId={user?.id} />
                </li>
              ) : (
                <li key={item.comment.id}>
                  <Comment
                    comment={item.comment}
                    members={members.data ?? []}
                    // Deleting someone else's comment needs `admin`; the server
                    // decides either way, so a wrong guess here costs a button.
                    canDelete={item.comment.author_id === user?.id || allows(role, 'admin')}
                    onDelete={() => remove.mutate(item.comment.id)}
                    deleting={remove.isPending && remove.variables === item.comment.id}
                    // Only the author rewrites a comment. An admin can take one
                    // down, but putting words in someone's mouth is another thing.
                    canEdit={item.comment.author_id === user?.id}
                    onEdit={(body) => edit.mutateAsync({ commentId: item.comment.id, body })}
                  />
                </li>
              ),
            )}
          </ul>
        )}

        <Composer
          members={members.data ?? []}
          placeholder="Leave a comment. Type @ to bring a colleague in."
          submitLabel="Comment"
          onSubmit={(body) => post.mutateAsync(body)}
        />
      </CardContent>
    </Card>
  );
}

const ACTIVITY_ICONS: Record<IssueActivity['kind'], LucideIcon> = {
  resolved: CircleCheck,
  unresolved: RotateCcw,
  ignored: BellOff,
  assigned: UserPlus,
  unassigned: UserMinus,
  regressed: RotateCcw,
  ignore_expired: RotateCcw,
  assignee_lost_access: UserMinus,
};

/** One change to the issue, as a line between the comments. */
function ActivityRow({ entry, youId }: { entry: IssueActivity; youId?: string }) {
  const Icon = ACTIVITY_ICONS[entry.kind];
  const actor = activityActor(entry, youId);

  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      {/* Centred under the comment avatars, so the thread reads as one column. */}
      <span className="grid size-6 shrink-0 place-items-center">
        <Icon className="size-3.5" />
      </span>
      <p className="min-w-0 flex-1">
        {actor && <span className="font-medium text-foreground">{actor} </span>}
        {describeActivity(entry, youId)}
        {!isAutomatic(entry) && entry.via && (
          <>
            {' '}
            <ViaBadge name={entry.via} />
          </>
        )}
        <span className="ml-2" title={absoluteTime(entry.created_at)}>
          {relativeTime(entry.created_at)}
        </span>
      </p>
    </div>
  );
}

/**
 * Marks what an application did on someone's behalf.
 *
 * The name is whatever the application registered and the user approved on the
 * consent screen — useful to recognise, but not proof of anything — so it is
 * shown as the text it is.
 */
function ViaBadge({ name }: { name: string }) {
  return (
    <Badge
      variant="muted"
      className="gap-1 px-1.5 py-0 align-middle font-normal"
      title={`Done through ${name}, an application acting on this person's behalf`}
    >
      <Bot className="size-3" />
      via {name}
    </Badge>
  );
}

function Comment({
  comment,
  members,
  canDelete,
  onDelete,
  deleting,
  canEdit,
  onEdit,
}: {
  comment: IssueComment;
  members: ProjectMember[];
  canDelete: boolean;
  onDelete: () => void;
  deleting: boolean;
  canEdit: boolean;
  onEdit: (body: string) => Promise<unknown>;
}) {
  const [editing, setEditing] = useState(false);
  // An account removed from the installation leaves its comments behind, so
  // every field about the author can be missing at once.
  const author = {
    name: comment.author_name ?? '',
    email: comment.author_email ?? '',
  };
  const named = comment.author_name || comment.author_email || 'Former member';

  return (
    <div className={cn('flex gap-3', deleting && 'opacity-50')}>
      <AssigneeAvatar member={author} className="mt-0.5 size-6 text-[10px]" />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-baseline gap-2 text-sm">
          <span className="min-w-0 truncate font-medium">{named}</span>
          <span className="text-xs text-muted-foreground" title={absoluteTime(comment.created_at)}>
            {relativeTime(comment.created_at)}
          </span>
          {comment.edited_at && (
            <span className="text-xs text-muted-foreground" title={absoluteTime(comment.edited_at)}>
              edited
            </span>
          )}
          {comment.via && <ViaBadge name={comment.via} />}
          {canEdit && !editing && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-6 px-2 text-muted-foreground"
              onClick={() => setEditing(true)}
              aria-label="Edit comment"
              title="Edit comment"
            >
              <Pencil />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'h-6 px-2 text-muted-foreground hover:text-destructive',
                // Whichever button comes first pushes the pair to the right.
                (!canEdit || editing) && 'ml-auto',
              )}
              onClick={onDelete}
              disabled={deleting}
              aria-label="Delete comment"
              title="Delete comment"
            >
              <Trash2 />
            </Button>
          )}
        </div>
        {editing ? (
          <Composer
            members={members}
            initialValue={comment.body}
            placeholder="Say what changed."
            submitLabel="Save"
            autoFocus
            onCancel={() => setEditing(false)}
            onSubmit={async (body) => {
              await onEdit(body);
              setEditing(false);
            }}
          />
        ) : (
          <CommentBody body={comment.body} mentions={comment.mentions} />
        )}
      </div>
    </div>
  );
}

/**
 * A comment body: whitespace preserved, mentions marked.
 *
 * Nothing is interpreted as markup — the text is rendered as text, which is
 * also why a stack trace pasted in keeps its shape.
 */
function CommentBody({ body, mentions }: { body: string; mentions: CommentMention[] }) {
  const { user } = useAuth();

  return (
    <p className="whitespace-pre-wrap break-words text-sm">
      {splitMentions(body, mentions).map((segment, index) =>
        segment.kind === 'text' ? (
          <span key={index}>{segment.value}</span>
        ) : (
          <Mention
            key={index}
            mention={segment.mention}
            isYou={segment.mention.user_id === user?.id}
          />
        ),
      )}
    </p>
  );
}

function Mention({ mention, isYou }: { mention: CommentMention; isYou: boolean }) {
  return (
    <span
      // The address, because two colleagues can go by the same name and the
      // chip has to be able to say which one this is.
      title={mention.email}
      className={cn(
        'rounded px-1 py-0.5 font-medium',
        isYou ? 'bg-brand-subtle text-brand-strong' : 'bg-muted text-foreground',
      )}
    >
      {/* Who they are now, not how the comment spelled them: a renamed
          colleague should be recognisable in the threads that named them. */}
      @{memberName(mention)}
    </span>
  );
}

/**
 * A comment box with `@` completion.
 *
 * The menu opens on the mention being typed rather than on a keystroke, so it
 * behaves the same whether the address was typed, pasted or half-corrected.
 */
function Composer({
  members,
  initialValue = '',
  placeholder,
  submitLabel,
  autoFocus = false,
  onCancel,
  onSubmit,
}: {
  members: ProjectMember[];
  initialValue?: string;
  placeholder: string;
  submitLabel: string;
  autoFocus?: boolean;
  onCancel?: () => void;
  onSubmit: (body: string) => Promise<unknown>;
}) {
  const input = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState(initialValue);
  const [mention, setMention] = useState<ActiveMention | null>(null);
  const [highlighted, setHighlighted] = useState(0);
  const [pending, setPending] = useState(false);
  // Kept here rather than read from the mutation: what the writer needs is the
  // reason their text was refused, next to the text, which is still in the box.
  const [failure, setFailure] = useState<Error | null>(null);

  const matches = mention ? matching(members, mention.query).slice(0, SUGGESTIONS) : [];
  const open = matches.length > 0;

  /**
   * Recompute which mention the caret is in.
   *
   * Deliberately never writes `value`: the textarea is controlled, and a caret
   * event can fire before React has flushed the last change, so reading the
   * text back from the element would occasionally undo a completion.
   */
  const track = (element: HTMLTextAreaElement) =>
    setMention(activeMention(element.value, element.selectionStart));

  const accept = (member: ProjectMember) => {
    const element = input.current;
    if (!element || !mention) return;

    // By name where that is unambiguous, by address where it is not: the
    // server refuses to guess between two colleagues spelled the same way.
    const completed = completeMention(
      value,
      mention,
      element.selectionStart,
      spell(member, members),
    );
    setValue(completed.value);
    setMention(null);
    // The caret has to be restored after React has written the new value, or
    // the browser leaves it at the end of the text.
    requestAnimationFrame(() => {
      element.focus();
      element.setSelectionRange(completed.caret, completed.caret);
    });
  };

  const submit = async () => {
    const body = value.trim();
    if (!body || pending) return;

    setPending(true);
    setFailure(null);
    try {
      await onSubmit(body);
      setValue(initialValue);
      setMention(null);
    } catch (error) {
      setFailure(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setPending(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (open) {
      // While the menu is up these keys belong to it. Enter in particular:
      // it must complete the mention, not post a half-written comment.
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : matches.length - 1;
        setHighlighted((current) => (current + step) % matches.length);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        const member = matches[highlighted];
        if (member) {
          event.preventDefault();
          accept(member);
          return;
        }
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setMention(null);
        return;
      }
    } else if (event.key === 'Escape' && onCancel) {
      // Only once the menu is out of the way: Escape closes the nearest thing.
      event.preventDefault();
      onCancel();
      return;
    }

    // The shortcut people expect from every other comment box.
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <Textarea
          ref={input}
          rows={3}
          autoFocus={autoFocus}
          value={value}
          placeholder={placeholder}
          disabled={pending}
          aria-label="Comment"
          onChange={(event) => {
            setValue(event.currentTarget.value);
            setHighlighted(0);
            track(event.currentTarget);
          }}
          // Every caret move, however it happened: arrow keys, a click, or the
          // completion below putting it after the address it just inserted.
          onSelect={(event) => track(event.currentTarget)}
          onBlur={() => setMention(null)}
          onKeyDown={onKeyDown}
        />

        {open && (
          <ul
            role="listbox"
            aria-label="People you can mention"
            className="absolute z-10 mt-1 max-h-60 w-72 max-w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md"
          >
            {matches.map((member, index) => (
              <li key={member.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={index === highlighted}
                  // The textarea loses focus on mousedown, which would close
                  // the menu before the click ever landed.
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setHighlighted(index)}
                  onClick={() => accept(member)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
                    index === highlighted && 'bg-accent text-accent-foreground',
                  )}
                >
                  <AssigneeAvatar member={member} />
                  <span className="min-w-0">
                    <span className="block truncate">{memberName(member)}</span>
                    {memberName(member) !== member.email && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {member.email}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {failure && <p className="text-sm text-destructive">{failure.message}</p>}

      <div className="flex items-center justify-end gap-3">
        {/* Not on a touch screen, which usually has no ⌘ key to press. */}
        <span className="text-xs text-muted-foreground pointer-coarse:hidden">
          {/* Named here rather than in a tooltip: it is the only way to post
              without reaching for the mouse. */}
          ⌘↵ to post
        </span>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
        )}
        <Button onClick={() => void submit()} disabled={pending || value.trim().length === 0}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

/**
 * People whose name or address matches what has been typed after the `@`.
 *
 * Everyone is offered on an empty query — a bare `@` is "who is there?" — and
 * whoever the query *starts* is offered first, name before address, since
 * someone typing `@gr` is spelling the beginning of something.
 */
function matching(members: ProjectMember[], query: string): ProjectMember[] {
  if (!query) return members;

  const matches = members.filter(
    (member) =>
      member.name.toLowerCase().includes(query) || member.email.toLowerCase().includes(query),
  );
  const rank = (member: ProjectMember) =>
    Number(member.name.toLowerCase().startsWith(query)) * 2 +
    Number(member.email.toLowerCase().startsWith(query));

  return matches.sort((a, b) => rank(b) - rank(a));
}
